/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { customElement, html, property } from 'lit-element';
import { findPeople, getEmailFromGraphEntity } from '../../graph/graph.people';
import { getPersonImage } from '../../graph/graph.photos';
import { getUserPresence } from '../../graph/graph.presence';
import { getUserWithPhoto } from '../../graph/graph.user';
import { Providers } from '../../Providers';
import { ProviderState } from '../../providers/IProvider';
import { getSvg, SvgIcon } from '../../utils/SvgHelper';
import { TeamsHelper } from '../../utils/TeamsHelper';
import { MgtTemplatedComponent } from '../templatedComponent';
import { styles } from './mgt-person-card-css';
/**
 * Web Component used to show detailed data for a person in the
 * Microsoft Graph
 *
 * @export
 * @class MgtPersonCard
 * @extends {MgtTemplatedComponent}
 *
 * @cssprop --person-card-display-name-font-size - {Length} Font size of display name title
 * @cssprop --person-card-display-name-color - {Color} Color of display name font
 * @cssprop --person-card-title-font-size - {Length} Font size of title
 * @cssprop --person-card-title-color - {Color} Color of title
 * @cssprop --person-card-subtitle-font-size - {Length} Font size of subtitle
 * @cssprop --person-card-subtitle-color - {Color} Color of subttitle
 * @cssprop --person-card-details-title-font-size - {Length} Font size additional details title
 * @cssprop --person-card-details-title-color- {Color} Color of additional details title
 * @cssprop --person-card-details-item-font-size - {Length} Font size items in additional details section
 * @cssprop --person-card-details-item-color - {Color} Color of items in additional details section
 * @cssprop --person-card-background-color - {Color} Color of person card background
 */
let MgtPersonCard = class MgtPersonCard extends MgtTemplatedComponent {
    /**
     * Array of styles to apply to the element. The styles should be defined
     * using the `css` tag function.
     */
    static get styles() {
        return styles;
    }
    /**
     * Set the person details to render
     *
     * @type {IDynamicPerson}
     * @memberof MgtPersonCard
     */
    get personDetails() {
        return this._personDetails;
    }
    set personDetails(value) {
        if (this._personDetails === value) {
            return;
        }
        this._personDetails = value;
        this.requestStateUpdate();
        this.requestUpdate('personDetails');
    }
    /**
     * Invoked each time the custom element is appended into a document-connected element
     *
     * @memberof MgtPersonCard
     */
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', e => e.stopPropagation());
    }
    /**
     * Synchronizes property values when attributes change.
     *
     * @param {*} name
     * @param {*} oldValue
     * @param {*} newValue
     * @memberof MgtPersonCard
     */
    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        if (oldValue === newValue) {
            return;
        }
        if (name === 'is-expanded') {
            this.isExpanded = false;
        }
        switch (name) {
            case 'person-query':
            case 'user-id':
                this.personDetails = null;
                this.requestStateUpdate();
                break;
        }
    }
    /**
     * Invoked on each update to perform rendering tasks. This method must return
     * a lit-html TemplateResult. Setting properties inside this method will *not*
     * trigger the element to update.
     */
    render() {
        // Handle no data
        if (!this.personDetails) {
            return this.renderNoData();
        }
        const person = this.personDetails;
        const image = this.getImage();
        const presence = this.personPresence;
        const showPresence = this.showPresence;
        // Check for a default template.
        // tslint:disable-next-line: no-string-literal
        if (this.hasTemplate('default')) {
            return this.renderTemplate('default', {
                person: this.personDetails,
                personImage: image
            });
        }
        // Check for a person-details template
        let personDetailsTemplate = this.renderTemplate('person-details', {
            person: this.personDetails,
            personImage: image
        });
        if (!personDetailsTemplate) {
            const personImageTemplate = this.renderPersonImage(image, presence, showPresence);
            const personNameTemplate = this.renderPersonName(person);
            const personTitleTemplate = this.renderPersonTitle(person);
            const personSubtitleTemplate = this.renderPersonSubtitle(person);
            const contactIconsTemplate = this.renderContactIcons(person);
            personDetailsTemplate = html `
        <div class="image">
          ${personImageTemplate}
        </div>
        <div class="details">
          ${personNameTemplate} ${personTitleTemplate} ${personSubtitleTemplate}
          <div class="base-icons">
            ${contactIconsTemplate}
          </div>
        </div>
      `;
        }
        const expandedDetailsTemplate = this.isExpanded ? this.renderExpandedDetails() : this.renderExpandedDetailsButton();
        return html `
      <div class="root">
        <div class="person-details-container">
          ${personDetailsTemplate}
        </div>
        <div class="expanded-details-container">
          ${expandedDetailsTemplate}
        </div>
      </div>
    `;
    }
    /**
     * Render the state when no data is available.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderNoData() {
        return this.renderTemplate('no-data', null) || html ``;
    }
    /**
     * Render a display image for the person.
     *
     * @protected
     * @param {*} image
     * @memberof MgtPersonCard
     */
    renderPersonImage(imageSrc, presence, showPresence) {
        imageSrc = imageSrc || this.getImage();
        presence = presence || this.personPresence;
        showPresence = showPresence || this.showPresence;
        const avatarSize = 'large';
        return html `
      <mgt-person
        class="person-image"
        .personDetails=${this.personDetails}
        .personImage=${imageSrc}
        .personPresence=${presence}
        .showPresence=${showPresence}
        .avatarSize=${avatarSize}
      ></mgt-person>
    `;
    }
    /**
     * Render the display name and persona details (e.g. department, job title) for a person.
     *
     * @protected
     * @param {IDynamicPerson} [person]
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderPersonName(person) {
        person = person || this.personDetails;
        return html `
      <div class="display-name" title="${person.displayName}">${person.displayName}</div>
    `;
    }
    /**
     * Render person title.
     *
     * @protected
     * @param {IDynamicPerson} person
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderPersonTitle(person) {
        person = person || this.personDetails;
        if (!person.jobTitle) {
            return;
        }
        return html `
      <div class="job-title">${person.jobTitle}</div>
    `;
    }
    /**
     * Render person subtitle.
     *
     * @protected
     * @param {IDynamicPerson} person
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderPersonSubtitle(person) {
        person = person || this.personDetails;
        if (!person.department) {
            return;
        }
        return html `
      <div class="department">${person.department}</div>
    `;
    }
    /**
     * Render the various icons for contacting the person.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderContactIcons(person) {
        if (this.isExpanded) {
            return html ``;
        }
        person = person || this.personDetails;
        const userPerson = person;
        const personPerson = person;
        // Chat
        let chat;
        if (userPerson.userPrincipalName) {
            chat = html `
        <div class="icon" @click=${() => this.chatUser()}>
          ${getSvg(SvgIcon.Chat, '#666666')}
        </div>
      `;
        }
        // Email
        let email;
        if (getEmailFromGraphEntity(person)) {
            email = html `
        <div class="icon" @click=${() => this.emailUser()}>
          ${getSvg(SvgIcon.Email, '#666666')}
        </div>
      `;
        }
        // Phone
        let phone;
        if ((userPerson.businessPhones && userPerson.businessPhones.length > 0) ||
            (personPerson.phones && personPerson.phones.length > 0)) {
            phone = html `
        <div class="icon" @click=${() => this.callUser()}>
          ${getSvg(SvgIcon.Phone, '#666666')}
        </div>
      `;
        }
        return html `
      ${chat} ${email} ${phone}
    `;
    }
    /**
     * Render the button used to expand the expanded details.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderExpandedDetailsButton() {
        return html `
      <div class="expanded-details-button" @click=${() => this.showExpandedDetails()}>
        <svg
          class="expanded-details-svg"
          width="16"
          height="15"
          viewBox="0 0 16 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M15 7L8.24324 13.7568L1.24324 6.75676" stroke="#3078CD" />
        </svg>
      </div>
    `;
    }
    /**
     * Render expanded details.
     *
     * @protected
     * @param {IDynamicPerson} [person]
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderExpandedDetails(person) {
        person = person || this.personDetails;
        const contactDetailsTemplate = this.renderContactDetails(person);
        const additionalDetailsTemplate = this.renderAdditionalDetails(person);
        const sectionDivider = additionalDetailsTemplate
            ? html `
          <div class="section-divider"></div>
        `
            : null;
        return html `
      ${contactDetailsTemplate} ${sectionDivider} ${additionalDetailsTemplate}
    `;
    }
    /**
     * Render additional details for the person.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderAdditionalDetails(person) {
        if (!this.hasTemplate('additional-details')) {
            return null;
        }
        person = person || this.personDetails;
        const personImage = this.getImage();
        const additionalDetailsTemplate = this.renderTemplate('additional-details', { person, personImage });
        return html `
      <div class="expanded-details-info">
        ${additionalDetailsTemplate}
      </div>
    `;
    }
    /**
     * Render the contact info part of the additional details.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderContactDetails(person) {
        person = person || this.personDetails;
        const userPerson = person;
        const personPerson = person;
        if (this.hasTemplate('contact-details')) {
            return this.renderTemplate('contact-details', { userPerson });
        }
        // Chat
        let chat;
        if (userPerson.userPrincipalName) {
            chat = html `
        <div class="details-icon" @click=${() => this.chatUser()}>
          ${getSvg(SvgIcon.SmallChat, '#666666')}
          <span class="link-subtitle data">${userPerson.userPrincipalName}</span>
        </div>
      `;
        }
        // Email
        let email;
        if (getEmailFromGraphEntity(person)) {
            email = html `
        <div class="details-icon" @click=${() => this.emailUser()}>
          ${getSvg(SvgIcon.SmallEmail, '#666666')}
          <span class="link-subtitle data">${getEmailFromGraphEntity(person)}</span>
        </div>
      `;
        }
        // Phone
        let phone;
        if (userPerson.businessPhones && userPerson.businessPhones.length > 0) {
            phone = html `
        <div class="details-icon" @click=${() => this.callUser()}>
          ${getSvg(SvgIcon.SmallPhone, '#666666')}
          <span class="link-subtitle data">${userPerson.businessPhones[0]}</span>
        </div>
      `;
        }
        else if (personPerson.phones && personPerson.phones.length > 0) {
            const businessPhones = this.getPersonBusinessPhones(personPerson);
            phone = html `
        <div class="details-icon" @click=${() => this.callUser()}>
          ${getSvg(SvgIcon.SmallPhone, '#666666')}
          <span class="link-subtitle data">${businessPhones[0]}</span>
        </div>
      `;
        }
        // Location
        let location;
        if (person.officeLocation) {
            location = html `
        <div class="details-icon">
          ${getSvg(SvgIcon.SmallLocation, '#666666')}<span class="normal-subtitle data">${person.officeLocation}</span>
        </div>
      `;
        }
        return html `
      <div class="expanded-details-info">
        <div class="contact-text">Contact</div>
        <div class="icons">
          ${chat} ${email} ${phone} ${location}
        </div>
      </div>
    `;
    }
    /**
     * load state into the component
     *
     * @protected
     * @returns
     * @memberof MgtPersonCard
     */
    loadState() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.personDetails && this.inheritDetails) {
                // User person details inherited from parent tree
                let parent = this.parentElement;
                while (parent && parent.tagName !== 'MGT-PERSON') {
                    parent = parent.parentElement;
                }
                if (parent && parent.personDetails) {
                    this.personDetails = parent.personDetails;
                    this.personImage = parent.personImage;
                    this.personPresence = parent.personPresence;
                    this.showPresence = parent.showPresence;
                }
            }
            const provider = Providers.globalProvider;
            // check if user is signed in
            if (!provider || provider.state !== ProviderState.SignedIn) {
                return;
            }
            const graph = provider.graph.forComponent(this);
            // check if personDetail already populated
            if (this.personDetails) {
                const user = this.personDetails;
                const id = user.userPrincipalName || user.id;
                // if we have an id but no email, we should get data from the graph
                if (id && !getEmailFromGraphEntity(user)) {
                    const person = yield getUserWithPhoto(graph, id);
                    this.personDetails = person;
                    this.personImage = this.getImage();
                }
                else if (!this.personDetails.personImage &&
                    ((this.fetchImage && !this.personImage) || this.personImage === '@')) {
                    // in some cases we might only have name or email, but need to find the image
                    const image = yield getPersonImage(graph, this.personDetails);
                    if (image) {
                        this.personDetails.personImage = image;
                        this.personImage = image;
                    }
                }
            }
            else if (this.userId || this.personQuery === 'me') {
                // Use userId or 'me' query to get the person and image
                const person = yield getUserWithPhoto(graph, this.userId);
                this.personDetails = person;
                this.personImage = this.getImage();
            }
            else if (this.personQuery) {
                // Use the personQuery to find our person.
                const people = yield findPeople(graph, this.personQuery, 1);
                if (people && people.length) {
                    this.personDetails = people[0];
                    const image = yield getPersonImage(graph, this.personDetails);
                    if (image) {
                        this.personDetails.personImage = image;
                        this.personImage = image;
                    }
                }
            }
            // populate presence
            const defaultPresence = {
                activity: 'Offline',
                availability: 'Offline',
                id: null
            };
            if (!this.personPresence && this.showPresence) {
                try {
                    if (this.personDetails && this.personDetails.id) {
                        this.personPresence = yield getUserPresence(graph, this.personDetails.id);
                    }
                    else {
                        this.personPresence = defaultPresence;
                    }
                }
                catch (_) {
                    // set up a default Presence in case beta api changes or getting error code
                    this.personPresence = defaultPresence;
                }
            }
        });
    }
    /**
     * Use the mailto: protocol to initiate a new email to the user.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    emailUser() {
        const user = this.personDetails;
        if (user) {
            const email = getEmailFromGraphEntity(user);
            if (email) {
                window.open('mailto:' + email, '_blank');
            }
        }
    }
    /**
     * Use the tel: protocol to initiate a new call to the user.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    callUser() {
        const user = this.personDetails;
        const person = this.personDetails;
        if (user && user.businessPhones && user.businessPhones.length) {
            const phone = user.businessPhones[0];
            if (phone) {
                window.open('tel:' + phone, '_blank');
            }
        }
        else if (person && person.phones && person.phones.length) {
            const businessPhones = this.getPersonBusinessPhones(person);
            const phone = businessPhones[0];
            if (phone) {
                window.open('tel:' + phone, '_blank');
            }
        }
    }
    /**
     * Initiate a chat message to the user via deeplink.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    chatUser() {
        const user = this.personDetails;
        if (user && user.userPrincipalName) {
            const users = user.userPrincipalName;
            const url = `https://teams.microsoft.com/l/chat/0/0?users=${users}`;
            const openWindow = () => window.open(url, '_blank');
            if (TeamsHelper.isAvailable) {
                TeamsHelper.executeDeepLink(url, (status) => {
                    if (!status) {
                        openWindow();
                    }
                });
            }
            else {
                openWindow();
            }
        }
    }
    /**
     * Display the expanded details panel.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    showExpandedDetails() {
        const root = this.renderRoot.querySelector('.root');
        if (root && root.animate) {
            // play back
            root.animate([
                {
                    height: 'auto',
                    transformOrigin: 'top left'
                },
                {
                    height: 'auto',
                    transformOrigin: 'top left'
                }
            ], {
                duration: 1000,
                easing: 'ease-in-out',
                fill: 'both'
            });
        }
        this.isExpanded = true;
    }
    getImage() {
        if (this.personImage && this.personImage !== '@') {
            return this.personImage;
        }
        const person = this.personDetails;
        return person && person.personImage ? person.personImage : null;
    }
    getPersonBusinessPhones(person) {
        const phones = person.phones;
        const businessPhones = [];
        for (const p of phones) {
            if (p.type === 'business') {
                businessPhones.push(p.number);
            }
        }
        return businessPhones;
    }
};
__decorate([
    property({
        attribute: 'person-query'
    })
], MgtPersonCard.prototype, "personQuery", void 0);
__decorate([
    property({
        attribute: 'user-id'
    })
], MgtPersonCard.prototype, "userId", void 0);
__decorate([
    property({
        attribute: 'person-details',
        type: Object
    })
], MgtPersonCard.prototype, "personDetails", null);
__decorate([
    property({
        attribute: 'person-image',
        type: String
    })
], MgtPersonCard.prototype, "personImage", void 0);
__decorate([
    property({
        attribute: 'fetch-image',
        type: Boolean
    })
], MgtPersonCard.prototype, "fetchImage", void 0);
__decorate([
    property({
        attribute: 'is-expanded',
        type: Boolean
    })
], MgtPersonCard.prototype, "isExpanded", void 0);
__decorate([
    property({
        attribute: 'inherit-details',
        type: Boolean
    })
], MgtPersonCard.prototype, "inheritDetails", void 0);
__decorate([
    property({
        attribute: 'show-presence',
        type: Boolean
    })
], MgtPersonCard.prototype, "showPresence", void 0);
__decorate([
    property({
        attribute: 'person-presence',
        type: Object
    })
], MgtPersonCard.prototype, "personPresence", void 0);
MgtPersonCard = __decorate([
    customElement('mgt-person-card')
], MgtPersonCard);
export { MgtPersonCard };
//# sourceMappingURL=mgt-person-card.js.map